import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, Outlet } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';

import { DashboardLayout } from './components/Layouts/Dashboard';
import { DriverLayout } from './components/Layouts/Driver';

import { ManagerGuard, DriverGuard } from 'guards';

import { userSelector } from 'store/user/selectors';

import useSelector from 'hooks/useSelector';

import { DashboardPage } from 'routes/Dashboard/DashboardPage';
import { DriversPage } from 'routes/Dashboard/DriversPage';
import { DriversReviewPage } from 'routes/Dashboard/DriversReviewPage';

import { DriverDocumentsPage } from 'routes/Driver/DriverDocumentsPage';
import ScanPage from 'routes/Driver/ScanPage';

import { DriverLogoutPage } from 'routes/Public/DriverLogoutPage';
import { LogoutPage } from 'routes/Public/LogoutPage';
import { LoginPage } from 'routes/Public/LoginPage';
import { LoginByEmailPage } from 'routes/Public/LoginByEmailPage';
import { ConfirmEmailPage } from 'routes/Public/ConfirmEmailPage';
import { DriverLoginPage } from 'routes/Public/DriverLoginPage';
import { RegistrationByInviteLinkPage } from 'routes/Public/RegistrationByInviteLinkPage';
import { ResetPasswordPage } from 'routes/Public/ResetPasswordPage';
import { TokenLoginPage } from 'routes/Public/TokenLoginPage';
import { ResetPasswordByEmailPage } from 'routes/Public/ResetPasswordByEmailPage';

import { NotFoundPage } from 'routes/System/NotFoundPage';

import * as _paths from 'routes/paths';
import ReactDOM from 'react-dom';
import { ToastContainer } from 'react-toastify';
import domTargets from '../config/dom/domTargets';
import { CompanyContext } from '../index';
import EndOfTheFlow from 'features/Driver/LicenseCameraScan/EndOfTheFlow';
import UnsecuredDriverLoginPage from 'routes/Public/UnsecuredDriverLoginPage';
import DriverCheckListPage from 'routes/Driver/DriverCheckListPage';
import ExternalReportPage from 'routes/ExternalReportPage';
import OneMoreThing from 'features/Driver/LicenseCameraScan/OneMoreThing';
import DOTMedicalQuestions from '../features/Driver/LicenseCameraScan/DOTMedicalQuestionsPage';
import TemporaryLicensePage from '../features/Driver/LicenseCameraScan/TemporaryLicensePage';
import { ManagerProvider } from '../contexts/access/ManagerContext/ManagerProvider';

export const paths = _paths;

function App() {
  const user = useSelector(userSelector);
  const company = useContext(CompanyContext);

  const [profileOpen, setProfileOpen] = useState<boolean>(false);

  useEffect(() => {
    if (company.name) {
      document.title = `HaulSafe | ${company.name}`;
    } else {
      document.title = `HaulSafe`;
    }
  }, [company.name]);

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path={paths.DashboardPage}
          element={
            <ManagerProvider>
              <ManagerGuard>
                <DashboardLayout
                  user={user}
                  profileOpen={profileOpen}
                  setProfileOpen={(e) => setProfileOpen(e)}
                >
                  <Outlet />
                </DashboardLayout>
              </ManagerGuard>
            </ManagerProvider>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path={paths.DashboardDriversPage} element={<DriversPage />} />
          <Route path={paths.ReportPagePath} element={<ExternalReportPage />} />
          <Route
            path={paths.DashboardDriversReviewPage}
            element={<DriversReviewPage />}
          />
        </Route>
        <Route
          path={paths.DriverDocumentsPage}
          element={
            <DriverGuard>
              <DriverLayout hideLogo>
                <Outlet />
              </DriverLayout>
            </DriverGuard>
          }
        >
          <Route path={paths.ScanPage} element={<ScanPage />} />
          <Route
            path={paths.TemporaryLicensePage}
            element={<TemporaryLicensePage />}
          />
          <Route
            path={paths.DOTMedicalQuestionsPage}
            element={<DOTMedicalQuestions />}
          />
          <Route path={paths.OneMoreThingPagePath} element={<OneMoreThing />} />
          <Route path={paths.ThatSItPage} element={<EndOfTheFlow />} />
          <Route index element={<DriverDocumentsPage />} />
        </Route>

        <Route
          path={paths.DriverDocumentsPage}
          element={
            <DriverGuard>
              <DriverLayout hideLogo whiteBackground>
                <Outlet />
              </DriverLayout>
            </DriverGuard>
          }
        >
          <Route
            path={`${paths.DriverCheckListPagePath}/:candidateDocumentId/:step?`}
            element={<DriverCheckListPage />}
          />
        </Route>

        <Route path={paths.LogoutPage} element={<LogoutPage />} />
        <Route path={paths.DriverLogoutPage} element={<DriverLogoutPage />} />
        <Route path={paths.LoginPage} element={<LoginPage />} />
        <Route path={paths.LoginByEmailPage} element={<LoginByEmailPage />} />
        <Route path={paths.ConfirmEmailPage} element={<ConfirmEmailPage />} />
        <Route path={paths.DriverLoginPage} element={<DriverLoginPage />} />
        <Route
          path={paths.UnsecuredDriverLoginPage}
          element={<UnsecuredDriverLoginPage />}
        />
        <Route
          path={paths.RegistrationByInviteLinkPage}
          element={<RegistrationByInviteLinkPage />}
        />
        <Route path={paths.ResetPasswordPage} element={<ResetPasswordPage />} />
        <Route
          path={paths.ResetPasswordByEmailPage}
          element={<ResetPasswordByEmailPage />}
        />
        <Route path={paths.TokenLoginPage} element={<TokenLoginPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {ReactDOM.createPortal(
        <ToastContainer
          hideProgressBar
          draggable={false}
          toastClassName="Toastify__toast-theme--colored"
        />,
        domTargets.toastTarget
      )}
    </>
  );
}

export default App;
